import React, { useEffect, useState } from 'react'
import { ActuatorPayload, SolarActuatorForm, SolarActuator as SolarActuatorModel } from '../../models/smart-home.model'
import MultilineActorBase from '../base/MultilineActorBase'
import { useAutoActuatorHeaderContent } from '../../hooks/use-auto-actuator-header-content'
import { useActuatorUpdate } from '../../hooks/use-actuator-update'
import { FormProvider, useForm } from 'react-hook-form'
import { useSolarActuatorFormInitializer } from '../../hooks'
import { zodResolver } from '@hookform/resolvers/zod'
import { Collapse, Divider, Stack } from '@mui/material'
import ActuatorOption from '../base/ActuatorOption'
import { LoadingButton } from '@mui/lab'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import ActuatorAutoOption from '../base/ActuatorAutoOption'
import ActuatorRepeatOption from '../base/ActuatorRepeatOption'

interface Props {
  serialNo: number
  actuator: SolarActuatorModel
}

const SolarActuator: React.FC<Props> = ({ serialNo, actuator }) => {
  const { initialValues, validationSchema } = useSolarActuatorFormInitializer(actuator)
  const [selectedState, setSelectedState] = useState(actuator.state === 'on')
  const [showAutoOptions, setShowAutoOptions] = useState(actuator.descriptions.auto ?? false)
  const [isLoading, setIsLoading] = useState(false)
  const onUpdateActuator = useActuatorUpdate({ setIsLoading })
  const { t } = useCustomTranslation()

  const headerContent = useAutoActuatorHeaderContent({
    serialNo,
    actuator,
    showAutoOptions,
    isLoading,
    selectedState,
    setIsLoading,
    setSelectedState,
  })

  const methods = useForm<SolarActuatorForm>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  })
  const autoValue = methods.watch('auto')

  useEffect(() => {
    if (autoValue || (!autoValue && actuator.descriptions.auto)) {
      setShowAutoOptions(true)
    } else {
      setShowAutoOptions(false)
    }
  }, [autoValue, actuator.descriptions.auto])

  const onSubmit = (values: SolarActuatorForm): void => {
    const payload: ActuatorPayload = { command: values.auto ? 'auto' : 'manual', descriptions: { ...values } }
    onUpdateActuator(serialNo, actuator, payload)
  }

  return (
    <FormProvider {...methods}>
      <MultilineActorBase actuator={actuator} headerContent={headerContent}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack
            sx={{ backgroundColor: 'background.paper2', borderColor: 'grey.grey06', py: 2, px: 4, mt: 2 }}
            borderRadius={theme => `${theme.shape.borderRadius}px`}
            gap={2}
            border={1}
          >
            <ActuatorAutoOption />
            <Collapse in={showAutoOptions} unmountOnExit>
              <Stack gap={2}>
                <ActuatorOption title={'smart-home.solar.min-on.title'} description={'smart-home.solar.min-on.description'}>
                  <TextField
                    name="minOn"
                    mask={'##:##'}
                    fullWidth={false}
                    clearable={false}
                    sx={{ mb: 2, maxWidth: { xs: 'auto', sm: 230 } }}
                    suffix={t('affixes.hours')}
                  />
                </ActuatorOption>
                <ActuatorOption title={'smart-home.solar.min-off.title'} description={'smart-home.solar.min-off.description'}>
                  <TextField
                    name="minOff"
                    mask={'##:##'}
                    fullWidth={false}
                    clearable={false}
                    sx={{ mb: 2, maxWidth: { xs: 'auto', sm: 230 } }}
                    suffix={t('affixes.hours')}
                  />
                </ActuatorOption>
                <ActuatorOption title={'smart-home.solar.used-power.title'} description={'smart-home.solar.used-power.description'}>
                  <TextField
                    name="usedPower"
                    fullWidth={false}
                    clearable={false}
                    sx={{ mb: 2, maxWidth: { xs: 'auto', sm: 230 } }}
                    suffix={t('affixes.watt')}
                  />
                </ActuatorOption>
                <ActuatorRepeatOption />
                <Divider sx={{ mx: -4, borderColor: 'grey.grey06' }} />
                <Stack direction="row" justifyContent="flex-end" gap={2}>
                  <LoadingButton variant="contained" type="submit" disabled={!methods.formState.isDirty} loading={isLoading}>
                    {t('common.save')}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        </form>
      </MultilineActorBase>
    </FormProvider>
  )
}

export default SolarActuator
